<script setup lang="ts">
import {
	useDefaultStore,
	type TMainCategory,
	type TMultiPlayerGameType,
} from "@/store";
import { IS_MULTIPLAYER_TEMPORARILY_DISABLED } from "@/assets/js/appConfig";
import { useWebSocketStore } from "@/store/websocket";
import { useToast } from "primevue/usetoast";

type TLinkedRouteName =
	| "MoviesPage"
	| "MusicPage"
	| "PeoplePage"
	| "MoviesLeaderboard"
	| "MusicLeaderboard"
	| "PeopleLeaderboard"
	| "MoviesSettingsPage"
	| "MusicSettingsPage"
	| "PeopleSettingsPage"
	| "MoviesClassicGamePage"
	| "MusicClassicGamePage"
	| "PeopleClassicGamePage"
	| "MoviesBeatTheClockPage"
	| "MusicBeatTheClockPage"
	| "PeopleBeatTheClockPage";

type TCategoryGameMode = {
	title: string;
	description: string;
	icon: string;
	routeName: TLinkedRouteName | null;
	disabled: boolean;
	isMultiplayer?: boolean;
	scores?: {
		best: number;
		last: number;
	};
};

const props = withDefaults(
	defineProps<{
		routeTargetLeaderboard:
			| "MusicLeaderboard"
			| "MoviesLeaderboard"
			| "PeopleLeaderboard"
			| null;
		routeTargetSettings:
			| "MusicSettingsPage"
			| "MoviesSettingsPage"
			| "PeopleSettingsPage"
			| null;
		categoryCode: TMainCategory;
		categoryName: "Music" | "Movies" | "People";
		gameModes: TCategoryGameMode[];
		fallbackRouteName: "MoviesPage" | "MusicPage" | "PeoplePage";
	}>(),
	{},
);

const store = useDefaultStore();
const wsStore = useWebSocketStore();
const toast = useToast();

const isVisibleCreateRoomDialog = ref(false);
const roomPassword = ref("");
const selectedRoomType = ref<TMultiPlayerGameType | null>(null);
const isCreatingRoom = ref(false);

function getRouteTarget(routeName: TLinkedRouteName | null): TLinkedRouteName {
	return routeName ?? props.fallbackRouteName;
}

const GAME_TOKEN_KEY = "game-token";

function clearGameToken() {
	sessionStorage.removeItem(GAME_TOKEN_KEY);
}

function isMultiplayerMode(mode: TCategoryGameMode) {
	return mode.isMultiplayer === true;
}

function openCreateRoomDialog(roomType: TMultiPlayerGameType) {
	if (IS_MULTIPLAYER_TEMPORARILY_DISABLED) {
		return;
	}

	selectedRoomType.value = roomType;
	roomPassword.value = "";
	isVisibleCreateRoomDialog.value = true;
}

function closeCreateRoomDialog() {
	isVisibleCreateRoomDialog.value = false;
	roomPassword.value = "";
	selectedRoomType.value = null;
}

const createRoomDialogTitle = computed(() => {
	if (selectedRoomType.value === store.MULTI_PLAYER_GAME_TYPES.WEAKEST_LINK) {
		return "Create Weakest Link Room";
	}

	return "Create Room";
});

async function createPrivateRoom() {
	if (!selectedRoomType.value) {
		return;
	}

	if (IS_MULTIPLAYER_TEMPORARILY_DISABLED) {
		return;
	}

	if (!store.playerToken) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Missing player token.",
			life: 3000,
		});
		return;
	}

	try {
		isCreatingRoom.value = true;
		store.multiplayerGameCategory = props.categoryCode;

		const params = {
			mpg_password: roomPassword.value.trim() || null,
			plr_token: store.playerToken,
			category: props.categoryCode,
			room_type: selectedRoomType.value,
		};

		await wsStore.safeSend({
			type: wsStore.MESSAGE_TYPES.CREATE_ROOM,
			data: params,
		});

		closeCreateRoomDialog();
	} catch (err: any) {
		console.warn(err?.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to create room.",
			life: 3000,
		});
	} finally {
		isCreatingRoom.value = false;
	}
}
</script>

<template>
	<section class="movies-page">
		<div class="overlay"></div>

		<div class="page-shell">
			<div class="content">
				<RouterLink
					to="/"
					class="back-link"
				>
					← Back
				</RouterLink>

				<div class="top-actions">
					<RouterLink
						:to="{ name: getRouteTarget(props.routeTargetLeaderboard) }"
						class="stats-link"
					>
						📊
					</RouterLink>

					<RouterLink
						:to="{ name: getRouteTarget(props.routeTargetSettings) }"
						class="settings-link"
						:class="{ 'is-disabled': !props.routeTargetSettings }"
						aria-label="Settings"
					>
						⚙
					</RouterLink>
				</div>

				<p class="eyebrow">{{ props.categoryName }}</p>
				<h1 class="title">Choose Your Mode</h1>

				<div class="modes-grid">
					<template
						v-for="mode in props.gameModes"
						:key="mode.title"
					>
						<RouterLink
							v-if="!isMultiplayerMode(mode)"
							:to="{ name: getRouteTarget(mode.routeName) }"
							class="mode-card"
							:class="{ 'is-disabled': mode.disabled || !mode.routeName }"
							@click="clearGameToken"
						>
							<div class="mode-icon">{{ mode.icon }}</div>

							<div class="mode-body">
								<h2 class="mode-title">
									{{ mode.title }}
									<span
										v-if="mode.disabled"
										class="coming-soon"
									>
										Soon
									</span>
								</h2>

								<p class="mode-description">
									{{ mode.description }}
								</p>

								<div
									v-if="mode.scores"
									class="mode-scores"
								>
									<div class="mode-score-card">
										<span class="mode-score-label">Best Score</span>
										<strong class="mode-score-value">
											{{ mode.scores.best }}
										</strong>
									</div>

									<div class="mode-score-card">
										<span class="mode-score-label">Last Score</span>
										<strong class="mode-score-value">
											{{ mode.scores.last }}
										</strong>
									</div>
								</div>
							</div>
						</RouterLink>

						<div
							v-else
							class="mode-card multiplayer-mode-card"
						>
							<div class="mode-icon">{{ mode.icon }}</div>

							<div class="mode-body">
								<h2 class="mode-title">
									{{ mode.title }}
								</h2>

								<p class="mode-description">
									{{ mode.description }}
								</p>

								<div class="multiplayer-grid">
									<button
										type="button"
										class="multiplayer-card"
										:class="{
											'is-disabled': IS_MULTIPLAYER_TEMPORARILY_DISABLED,
										}"
										:disabled="IS_MULTIPLAYER_TEMPORARILY_DISABLED"
										@click="
											openCreateRoomDialog(
												store.MULTI_PLAYER_GAME_TYPES.WEAKEST_LINK,
											)
										"
									>
										<div class="multiplayer-card__header">
											<span class="multiplayer-card__title">Weakest Link</span>
										</div>

										<span class="multiplayer-card__text">
											Compete until the weakest link is out.
										</span>
									</button>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>

		<Dialog
			v-model:visible="isVisibleCreateRoomDialog"
			modal
			:header="createRoomDialogTitle"
			:closable="!isCreatingRoom"
			:dismissableMask="!isCreatingRoom"
			@update:visible="!$event && closeCreateRoomDialog()"
			:pt="{
				root: { class: 'create-room-modal' },
				mask: { class: 'create-room-modal-mask' },
				header: { class: 'create-room-modal__header' },
				title: { class: 'create-room-modal__title' },
				content: { class: 'create-room-modal__content' },
				pcCloseButton: {
					root: { class: 'create-room-modal__close' },
				},
			}"
		>
			<div class="create-room-dialog">
				<div class="dialog-field">
					<label
						for="room-password"
						class="dialog-label"
					>
						Password (optional)
					</label>

					<input
						id="room-password"
						v-model="roomPassword"
						type="text"
						class="dialog-input"
						placeholder="Leave empty for no password"
						:disabled="isCreatingRoom"
					/>
				</div>

				<div class="dialog-actions">
					<button
						type="button"
						class="dialog-button dialog-button--secondary"
						:disabled="isCreatingRoom"
						@click="closeCreateRoomDialog"
					>
						Cancel
					</button>

					<button
						type="button"
						class="dialog-button dialog-button--primary"
						:disabled="isCreatingRoom || IS_MULTIPLAYER_TEMPORARILY_DISABLED"
						@click="createPrivateRoom"
					>
						{{ isCreatingRoom ? "Starting..." : "Start Game" }}
					</button>
				</div>
			</div>
		</Dialog>
	</section>
</template>
<style lang="scss" scoped>
.movies-page {
	position: relative;
	min-height: 100svh;
	max-height: 100%;
	overflow-y: auto;
	background: radial-gradient(
			circle at center,
			#2a2a2a 0%,
			#171717 46%,
			#070707 100%
		),
		#101010;
	// background-image: url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: fixed;
	inset: 0;
	pointer-events: none;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
	);
	z-index: 0;
}

.page-shell {
	position: relative;
	z-index: 1;
	width: 100%;
	min-height: 100dvh;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	padding: 24px 16px calc(72px + env(safe-area-inset-bottom));
	display: flex;
	align-items: flex-start;
	justify-content: center;
}

.content {
	width: 100%;
	max-width: 980px;
	margin-top: 50px;
	padding-bottom: 24px;
}

.back-link {
	position: absolute;
	top: 16px;
	left: 16px;
	z-index: 3;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 42px;
	padding: 0 14px;
	border-radius: 999px;
	text-decoration: none;
	font-size: 14px;
	font-weight: 700;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	color: $text-color-white;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.16);
		border-color: rgba(255, 255, 255, 0.24);
	}
}

.top-actions {
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 3;
	display: flex;
	align-items: center;
	gap: 10px;
}

.settings-link {
	width: 42px;
	padding: 0;
	font-size: 20px;
}

.stats-link {
	min-width: 42px;
}

.settings-link,
.stats-link {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 42px;
	padding: 0 14px;
	border-radius: 999px;
	text-decoration: none;
	font-size: 14px;
	font-weight: 700;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	color: $text-color-white;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.16);
		border-color: rgba(255, 255, 255, 0.24);
	}

	&.is-disabled {
		opacity: 0.5;
		cursor: not-allowed;
		pointer-events: none;
		filter: grayscale(0.2);
	}
}

.eyebrow {
	margin: 0 0 8px;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.16em;
	text-transform: uppercase;
	color: $text-color-grey;
	text-align: center;
}

.title {
	margin: 0;
	font-size: clamp(34px, 8vw, 56px);
	line-height: 0.95;
	font-weight: 900;
	letter-spacing: -0.03em;
	color: $text-color-white;
	text-align: center;
	text-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.modes-grid {
	margin-top: 24px;
	display: grid;
	grid-template-columns: 1fr;
	gap: 14px;
	padding-bottom: 8px;
}

.mode-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 14px;
	padding: 18px 16px;
	border-radius: 22px;
	text-decoration: none;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.16);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		opacity 0.2s ease;
}

.mode-card:not(.multiplayer-mode-card, .is-disabled):hover {
	background: rgba(255, 255, 255, 0.16);
	border-color: rgba(255, 255, 255, 0.24);
}

.mode-card.is-disabled {
	opacity: 0.5;
	cursor: not-allowed;
	filter: grayscale(0.2);
}

.mode-icon {
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 18px;
	font-size: 26px;
	background: rgba(255, 255, 255, 0.12);
	color: $text-color-white;
	flex: 0 0 auto;
}

.mode-body {
	width: 100%;
	min-width: 0;
}

.mode-title {
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 8px;
	font-size: 20px;
	font-weight: 800;
	color: $text-color-white;
}

.mode-description {
	margin: 8px 0 0;
	font-size: 14px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.78);
}

.coming-soon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	background: rgba(255, 255, 255, 0.12);
	color: $text-color-grey;
}

.mode-scores {
	margin-top: 14px;
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}

.mode-score-card {
	padding: 12px 14px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.1);
	text-align: center;
}

.mode-score-label {
	display: block;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: $text-color-grey;
}

.mode-score-value {
	display: block;
	margin-top: 8px;
	font-size: 22px;
	line-height: 1;
	font-weight: 900;
	color: $text-color-white;
}

.multiplayer-mode-card {
	cursor: default;
}

.multiplayer-grid {
	margin-top: 14px;
	display: grid;
	grid-template-columns: 1fr;
	gap: 10px;
	width: 100%;
}

.multiplayer-card {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	padding: 14px 16px;
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.08);
	color: $text-color-white;
	text-align: left;
	cursor: pointer;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;

	&:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.14);
		border-color: rgba(255, 255, 255, 0.2);
	}

	/* stylelint-disable-next-line no-descending-specificity */
	&:disabled,
	&.is-disabled {
		opacity: 0.5;
		cursor: not-allowed;
		filter: grayscale(0.2);
	}
}

.multiplayer-card__header {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
}

.multiplayer-card__title {
	font-size: 15px;
	font-weight: 800;
	line-height: 1.2;
}

.multiplayer-card__text {
	font-size: 13px;
	line-height: 1.45;
	color: rgba(255, 255, 255, 0.72);
}

@media (min-width: 641px) {
	.page-shell {
		padding: 32px 20px calc(72px + env(safe-area-inset-bottom));
		align-items: center;
	}

	.back-link {
		top: 20px;
		left: 20px;
		height: 46px;
	}

	.top-actions {
		top: 20px;
		right: 20px;
	}

	.settings-link,
	.stats-link {
		height: 46px;
	}

	.settings-link {
		width: 46px;
		font-size: 22px;
	}

	.eyebrow {
		font-size: 14px;
	}

	.title {
		font-size: clamp(40px, 7vw, 72px);
	}

	.modes-grid {
		margin-top: 32px;
		gap: 16px;
	}

	.mode-card {
		flex-direction: row;
		align-items: flex-start;
		text-align: left;
		gap: 18px;
		padding: 22px;
	}

	.mode-icon {
		width: 68px;
		height: 68px;
		font-size: 30px;
		flex: 0 0 68px;
		border-radius: 18px;
	}

	.mode-title {
		justify-content: flex-start;
		font-size: 24px;
	}

	.mode-description {
		font-size: 15px;
	}

	.mode-scores {
		gap: 12px;
	}

	.mode-score-card {
		padding: 14px 16px;
	}

	.mode-score-label {
		font-size: 11px;
	}

	.mode-score-value {
		font-size: 24px;
	}

	.multiplayer-grid {
		grid-template-columns: minmax(0, 1fr);
		gap: 12px;
	}

	.multiplayer-card {
		padding: 16px 18px;
	}

	.multiplayer-card__title {
		font-size: 16px;
	}

	.multiplayer-card__text {
		font-size: 14px;
	}
}

// dialog

:global(.create-room-modal-mask) {
	backdrop-filter: blur(6px);
	background: rgba(6, 8, 14, 0.56) !important;
}

:global(.create-room-modal) {
	width: min(92vw, 440px);
	border-radius: 24px !important;
	border: 1px solid rgba(255, 255, 255, 0.12) !important;
	background: rgba(14, 18, 28, 0.94) !important;
	backdrop-filter: blur(18px);
	box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35) !important;
	color: $text-color-white !important;
	overflow: hidden;
}

:global(.create-room-modal__header) {
	padding: 20px 20px 12px;
	background: transparent !important;
	border-bottom: 0 !important;
}

:global(.create-room-modal__title) {
	font-size: 28px;
	font-weight: 900;
	line-height: 1;
	letter-spacing: -0.03em;
	color: $text-color-white !important;
}

:global(.create-room-modal__content) {
	padding: 8px 20px 20px;
	background: transparent !important;
	color: $text-color-white !important;
}

:global(.create-room-modal__close) {
	width: 42px !important;
	height: 42px !important;
	border-radius: 999px !important;
	background: rgba(255, 255, 255, 0.06) !important;
	border: 1px solid rgba(255, 255, 255, 0.12) !important;
	color: rgba(255, 255, 255, 0.78) !important;
	box-shadow: none !important;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.1) !important;
		border-color: rgba(255, 255, 255, 0.18) !important;
		color: $text-color-white !important;
	}
}

.create-room-dialog {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.dialog-field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.dialog-label {
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: rgba(255, 255, 255, 0.68);
}

.dialog-input {
	width: 100%;
	height: 50px;
	padding: 0 14px;
	border-radius: 14px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	background: rgba(255, 255, 255, 0.08);
	color: $text-color-white;
	font-size: 15px;
	outline: none;
	box-shadow: none;
	backdrop-filter: blur(8px);
	transition:
		border-color 0.2s ease,
		background 0.2s ease,
		box-shadow 0.2s ease;

	&::placeholder {
		color: rgba(255, 255, 255, 0.42);
	}

	&:focus {
		border-color: rgba(255, 255, 255, 0.24);
		background: rgba(255, 255, 255, 0.1);
		box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.05);
	}
}

.dialog-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}

.dialog-button {
	height: 42px;
	padding: 0 16px;
	border: 0;
	border-radius: 999px;
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition:
		background 0.2s ease,
		opacity 0.2s ease,
		transform 0.2s ease;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}

.dialog-button--secondary {
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.88);

	&:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.12);
	}
}

.dialog-button--primary {
	background: rgba(255, 255, 255, 0.16);
	color: $text-color-white;

	&:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.22);
	}
}

@media (min-width: 641px) {
	:global(.create-room-modal__header) {
		padding: 22px 22px 12px;
	}

	:global(.create-room-modal__content) {
		padding: 8px 22px 22px;
	}
}
</style>
